/* eslint-disable @typescript-eslint/no-explicit-any */
import { message } from 'antd';
import axios from 'axios';
import { t } from 'i18next';

import { NONCE_SRV } from 'apis/hcp/users.api';
import env from 'configs/env';
import { hipaaResponse } from 'utils/constants';
import {
  clearAllCookies,
  clearSiteStorage,
  getService,
  getSigningKey,
  getToken,
  isAPIUS,
} from 'utils/storage';

const HCP = env.mode === 'production' ? env.hcp : env.hcpProxy;
const ACCOUNTS = env.mode === 'production' ? env.accounts : env.accountsProxy;
const IP = env.mode === 'production' ? env.ip : env.ipProxy;

const HCPUS = env.mode === 'production' ? env.hcpUS : env.hcpUSProxy;
const ACCOUNTSUS = env.mode === 'production' ? env.accountsUS : env.accountsUSProxy;
const ACCOUNTS_BASE_URL = env.accountsUS;

const IPUS = env.mode === 'production' ? env.ipUS : env.ipUSProxy;

const errorHandler = (error: any) => {
  if (
    error.response &&
    (error.response?.data?.message?.includes('Expired Token') ||
      error.response?.data?.message?.includes(hipaaResponse) ||
      (error.response?.status === 401 && error?.response?.config?.url?.includes('/me'))) &&
    !error?.response?.config?.url?.includes('/login')
  ) {
    message.warning(t('expired'));
    clearSiteStorage();
    setTimeout(() => {
      window.location.replace('/signin');
    }, 1500);
  }
  return Promise.reject(error);
};

export const apiHCPInstance = axios.create({
  withCredentials: true,
  baseURL: HCP,
});

export const apiAccountsInstance = axios.create({
  withCredentials: true,
  baseURL: ACCOUNTS,
});

export const apiIPInstance = axios.create({
  withCredentials: true,
  baseURL: IP,
});

apiAccountsInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return errorHandler(error);
  }
);

apiHCPInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return errorHandler(error);
  }
);

apiIPInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return errorHandler(error);
  }
);

export let apiHCPUSInstance = axios.create({
  withCredentials: true,
  baseURL: HCPUS,
});

export const apiAccountsUSInstance = axios.create({
  withCredentials: true,
  baseURL: ACCOUNTSUS,
});

export let apiAccountsInstanceExternal = axios.create({
  withCredentials: true,
  baseURL: ACCOUNTS_BASE_URL,
});

export const apiIPUSInstance = axios.create({
  withCredentials: true,
  baseURL: IPUS,
});

apiAccountsUSInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return errorHandler(error);
  }
);

apiHCPUSInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return errorHandler(error);
  }
);

apiIPUSInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return errorHandler(error);
  }
);

export const authHeaders = () => {
  const headers: { 'x-auth-token'?: string; 'x-signing-service': any; 'x-amzn-trace-id'?: string } =
    {
      'x-signing-service': getService() ?? 'HCP',
    };
  headers['x-auth-token'] = getToken() ?? '';
  if (env.mode === 'development') {
    headers['x-amzn-trace-id'] = 'Local';
  }
  return headers;
};

export const noneHeaders = () => {
  const headers: {
    nonce?: string;
    'x-signing-service': any;
    'x-signing-key'?: string;
    'x-amzn-trace-id'?: string;
  } = {
    'x-signing-service': getService('nonce') ?? 'HCP',
  };

  headers.nonce = getToken('nonce') ?? '';
  headers['x-signing-key'] = getSigningKey() ?? '';
  if (env.mode === 'development') {
    headers['x-amzn-trace-id'] = 'Local';
  }
  return headers;
};

export const hcpAPI = ({
  method,
  url,
  data,
  config = {},
}: {
  method: string;
  url: string;
  data?: any;
  config?: { headers?: Record<string, any>; [key: string]: any };
}) => {
  const combineHeaders = {
    ...authHeaders(),
    ...(config.headers || {}),
  };

  if (isAPIUS()) {
    return apiHCPUSInstance({
      method,
      url,
      data,
      ...config,
      headers: combineHeaders,
    });
  }
  return apiHCPInstance({
    method,
    url,
    data,
    ...config,
    headers: combineHeaders,
  });
};

export const nonceHcpAPI = ({
  method,
  url,
  data,
  config = {},
}: {
  method: string;
  url: string;
  data?: any;
  config?: object;
}) => {
  const apiServer = localStorage.getItem(NONCE_SRV);
  clearAllCookies();
  if (apiServer === 'US') {
    return apiHCPUSInstance({
      method,
      url,
      data,
      headers: { ...noneHeaders() },
      ...config,
    });
  }
  return apiHCPInstance({
    method,
    url,
    data,
    headers: { ...noneHeaders() },
    ...config,
  });
};

export const ipAPI = ({
  method,
  url,
  data,
  config = {},
}: {
  method: string;
  url: string;
  data?: any;
  config?: any;
}) => {
  if (isAPIUS()) {
    return apiIPUSInstance({
      method,
      url,
      data,
      headers: { ...authHeaders() },
      ...config,
    });
  }
  return apiIPInstance({
    method,
    url,
    data,
    headers: { ...authHeaders() },
    ...config,
  });
};

export const accountsAPI = ({
  method,
  url,
  data,
  config = {},
}: {
  method: string;
  url: string;
  data?: any;
  config?: any;
}) => {
  if (isAPIUS()) {
    return apiAccountsUSInstance({
      method,
      url,
      data,
      headers: { ...authHeaders() },
      ...config,
    });
  }
  return apiAccountsInstance({
    method,
    url,
    data,
    headers: { ...authHeaders() },
    ...config,
  });
};

function getApiInstanceWithToken(token: string, service: string | null) {
  const api = axios.create({
    baseURL: HCPUS,
    headers: {
      'x-auth-token': token,
      'x-signing-service': service,
    },
  });
  api.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (
        error.response &&
        error.response.status === 401 &&
        !error.response.config.url.endsWith('/me')
      ) {
        message.warning(t('expired'));
        clearSiteStorage();
        setTimeout(() => {
          window.location.replace('/signin');
        }, 1500);
      }
      return Promise.reject(error);
    }
  );
  return api;
}

export function setAuthToken(token: string | null, service: string | null): void {
  if (token) {
    apiHCPUSInstance = getApiInstanceWithToken(token, service);
    apiAccountsInstanceExternal = axios.create({
      withCredentials: true,
      baseURL: ACCOUNTS_BASE_URL,
      headers: {
        'x-auth-token': token,
        'x-signing-service': service,
      },
    });
  }
}
